import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import { api } from '../api';
import { toast } from 'sonner';
import { Shield, Clock, Eye, Edit, FileText, Trash2, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import PageLayout from '../components/PageLayout';

const ACTION_ICONS = {
  view: Eye,
  create: FileText,
  update: Edit,
  delete: Trash2,
  export: Download,
};

const ACTION_COLORS = {
  view: 'text-blue-600 bg-blue-50',
  create: 'text-green-600 bg-green-50',
  update: 'text-amber-600 bg-amber-50',
  delete: 'text-red-600 bg-red-50',
  export: 'text-purple-600 bg-purple-50',
};

const AuditLogsPage = () => {
  const { isAdmin } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    resource_type: '',
    days: '30',
  });

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.resource_type) params.set('resource_type', filters.resource_type);
      params.set('days', filters.days);
      params.set('limit', '200');
      const res = await api.get(`/admin/audit-logs?${params.toString()}`);
      setLogs(res.data.logs || []);
    } catch {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [filters.resource_type, filters.days]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <PageLayout title="Audit Logs" maxWidth="max-w-5xl">
      <div className="space-y-6">
          {/* Filters */}
          <Card>
            <CardContent className="py-4">
              <div className="flex flex-wrap gap-4 items-end">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-slate-500">Resource Type</label>
                  <Select
                    value={filters.resource_type || 'all'}
                    onValueChange={(v) => setFilters(f => ({ ...f, resource_type: v === 'all' ? '' : v }))}
                  >
                    <SelectTrigger className="w-[180px]" data-testid="audit-filter-resource">
                      <SelectValue placeholder="All types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All types</SelectItem>
                      <SelectItem value="full_profile">Full Profile View</SelectItem>
                      <SelectItem value="profile">Profile View</SelectItem>
                      <SelectItem value="passport">Passport</SelectItem>
                      <SelectItem value="medical">Medical</SelectItem>
                      <SelectItem value="license">License</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-slate-500">Time Range</label>
                  <Select
                    value={filters.days}
                    onValueChange={(v) => setFilters(f => ({ ...f, days: v }))}
                  >
                    <SelectTrigger className="w-[140px]" data-testid="audit-filter-days">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">Last 7 days</SelectItem>
                      <SelectItem value="30">Last 30 days</SelectItem>
                      <SelectItem value="90">Last 90 days</SelectItem>
                      <SelectItem value="365">Last year</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="outline" size="sm" onClick={fetchLogs} className="rounded-full" data-testid="audit-refresh-btn">
                  Refresh
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Logs */}
          <Card className="overflow-hidden">
            <CardHeader>
              <CardTitle className="text-lg font-heading">Activity Log</CardTitle>
              <CardDescription>{logs.length} event{logs.length !== 1 ? 's' : ''} found</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : logs.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <Shield className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No audit events found for the selected filters</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {logs.map((log, i) => {
                    const Icon = ACTION_ICONS[log.action] || Eye;
                    const colorClass = ACTION_COLORS[log.action] || 'text-slate-600 bg-slate-50';
                    return (
                      <div
                        key={i}
                        className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors border border-transparent hover:border-slate-100"
                        data-testid={`audit-log-entry-${i}`}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${colorClass}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0 overflow-hidden">
                          <p className="text-sm text-slate-800 break-words">
                            <span className="font-medium">{log.actor_name || 'Unknown'}</span>
                            {' '}
                            <span className="text-slate-500">
                              {log.action === 'view' ? 'viewed' : log.action === 'update' ? 'updated' : log.action}
                            </span>
                            {' '}
                            <span className="font-medium">{log.resource_type?.replace('_', ' ')}</span>
                            {log.details?.target_name && (
                              <span className="text-slate-500"> for {log.details.target_name}</span>
                            )}
                          </p>
                          <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDate(log.timestamp)}
                            </span>
                            {log.ip_address && (
                              <span>IP: {log.ip_address}</span>
                            )}
                            {log.actor_email && (
                              <span>{log.actor_email}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
    </PageLayout>
  );
};

export default AuditLogsPage;
